import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * Settings → Easeedesk → Emails dispatching.
 *
 * Perechile vechi: `settings/easeedesk/ticket_dispatching`,
 * `get_ticket_extra_sources`, `get_dispatcher_list`,
 * `get_dispatching_ticket_type_status`, `get_dispatching_ticket_subcategories`,
 * `get_ticket_type_status`, `save_ticket_dispatching`.
 *
 * `apiGet` / `apiPost` pun ELE `/api/v2` in fata caii.
 */

const BASE = '/settings/easeedesk/ticket_dispatching'

export interface DispatchCompany {
  ID: number
  name: string
}

export interface DispatchSource {
  ID: number
  source_name: string
  /** Sursa asta mai cere o casuta: care cutie de email, care fel de alerta. */
  extra_source: boolean
}

export interface DispatchingShell {
  title: string
  moduleUrl: string
  companies: DispatchCompany[]
  sources: DispatchSource[]
}

export interface SourceOption {
  value: number
  name: string
}

export interface NamedRow {
  ID: number
  name: string
}

export interface MarkedRow extends NamedRow {
  selected: boolean
}

export interface ExtraFieldOption {
  ID: number
  option_name: string
  option_value: string
}

export interface ExtraField {
  ID: number
  field_type: string
  values: ExtraFieldOption[]
}

export interface DispatcherOption {
  dispatcher_id: number
  dispatcher_name: string
  info: string
  /** Din ce lista se alege valoarea; null = repartizatorul n-are casuta. */
  select_field: string | null
  select_value: number | null
  checked: boolean
  /** 0 = setare de sus, peste linia punctata; >0 = locul in tragere. */
  order_id: number
  option_name: string | null
  option_value: string | null
  /** Numai pe setarile de sus: spune care casuta se reincarca prin ajax. */
  classes: string
  fields: ExtraField[]
}

export interface DispatcherList {
  company: number
  ticket_source: number
  source_option: number
  options: DispatcherOption[]
  ticket_types: NamedRow[]
  ticket_priorities: NamedRow[]
  ticket_categories: NamedRow[]
  groups: NamedRow[]
  administrators: NamedRow[]
}

export function dispatchingShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_dispatching'],
    queryFn: () => apiGet<DispatchingShell>(BASE),
  })
}

export function dispatchingExtraSourcesQuery(ticketSource: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_dispatching', 'extra_sources', ticketSource],
    queryFn: () =>
      apiGet<{ options: SourceOption[] }>(`${BASE}/get_ticket_extra_sources/${ticketSource}`),
  })
}

export function dispatcherListQuery(company: number, ticketSource: number, sourceOption: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_dispatching', 'list', company, ticketSource, sourceOption],
    queryFn: () =>
      apiGet<DispatcherList>(
        `${BASE}/get_dispatcher_list?company=${company}&ticket_source=${ticketSource}&source_option=${sourceOption}`,
      ),
  })
}

/** Statusurile tipului ales, cu cel deja scris bifat. */
export function dispatchingTypeStatuses(
  ticket_type: number,
  ticket_source: number,
  source_option: number,
  company: number,
) {
  return apiPost<{ statuses: MarkedRow[] }>(`${BASE}/get_dispatching_ticket_type_status`, {
    ticket_type,
    ticket_source,
    source_option,
    company,
  })
}

/** Subcategoriile categoriei alese, cu cea deja scrisa bifata. */
export function dispatchingSubcategories(
  ticket_category: number,
  ticket_source: number,
  source_option: number,
  company: number,
) {
  return apiPost<{ subcategories: MarkedRow[] }>(`${BASE}/get_dispatching_ticket_subcategories`, {
    ticket_category,
    ticket_source,
    source_option,
    company,
  })
}

export interface DispatcherSaveRow {
  dispatcher_id: number
  order_id: number
  value: number | null
  checked: boolean
}

/**
 * Salvarea. Formularul vechi trimitea `dispatcher[<id>][order_id|value|checked]`,
 * cu ID-ul repartizatorului drept CHEIE - nu un indice de sir. Cheile se scriu
 * aici de mana, fiindca `encodePost` stie numai siruri.
 */
export function saveTicketDispatching(
  company: number,
  ticket_source: number,
  source_option: number,
  rows: DispatcherSaveRow[],
) {
  const fields: Record<string, PostValue> = { company, ticket_source, source_option }

  for (const row of rows) {
    fields[`dispatcher[${row.dispatcher_id}][order_id]`] = row.order_id
    fields[`dispatcher[${row.dispatcher_id}][checked]`] = row.checked ? 1 : 0
    fields[`dispatcher[${row.dispatcher_id}][value]`] = row.value === null ? 0 : row.value
  }

  return apiPost<{ ok?: boolean }>(`${BASE}/save_ticket_dispatching`, fields)
}
